<i18n>
{
  "fr": {
    "compliance-no-data": "Aucun indicateur n'est disponible. Veuillez contacter un représentant d'AEMQ.",
    "compliance-target-date": "Pour le {date}",
    "compliance-noncompliances": "Gestion des non-conformités",
    "compliance-indicator-label": "Indicateurs",
    "compliance-documents-label": "Documents complétés",
    "compliance-progress-label": "Statut"

  },
  "en": {
    "compliance-no-data": "No indicator available. Please contact an AEMQ representative.",
    "compliance-target-date": "Due on {date}",
    "compliance-noncompliances": "Management of non compliances",
    "compliance-indicator-label": "Indicators",
    "compliance-documents-label": "Completed documents",
    "compliance-progress-label": "Status"
  }
}
</i18n>

<template>
  <div class="compliance">
    <s-loader :active="loading && !data" />
    <p v-if="data && data.indicatorGroups.length === 0">{{ $t('compliance-no-data') }}</p>
    <div class="compliance__container" v-if="data && data.indicatorGroups.length > 0">
      <div v-for="group in data.indicatorGroups" :key="group.groupName">
        <b-collapse
          v-if="!showSpecificOnly || (showSpecificOnly && groupIsActive(group))"
          :open.sync="collapseState[group.groupName]"
        >
          <div slot="trigger" slot-scope="props" class="card-header is-shadowless" role="button">
            <p
              class="card-header-title px-0"
              style="flex-wrap: wrap"
              :class="{
                'has-text-grey-light': !groupIsActive(group)
              }"
            >
              <span class="level" style="width: 100%">
                <span class="level-left" style="flex-shrink: 1">
                  <span class="mr-16px">{{ group.groupName }}</span> </span
                ><span class="level-right">
                  <status-badge-group
                    v-if="groupIsActive(group)"
                    :indicators="indicatorsGroupFiltered(group)"
                    :isSpecificCount="specificIndicatorCount(group)"
                  />
                </span>
              </span>
            </p>
            <span class="card-header-icon has-text-primary pr-0">
              <b-icon :icon="props.open ? 'chevron-up' : 'chevron-down'"></b-icon>
            </span>
          </div>
          <div
            class="card-content"
            :class="{
              'pb-16px pr-0 pt-0 pl-0': $media.mobile,
              'px-40px py-0': $media.tablet
            }"
          >
            <div class="compliance__table-wrapper">
              <!-- Table -->
              <b-table :data="sortIndicators(group.indicators)">
                <!-- Difficulty icon -->
                <b-table-column v-slot="{ row }" header-class="pb-8px pr-0" cell-class="difficulty-icon-cell">
                  <difficulty-icon :indicator="row" class="has-text-grey" />
                </b-table-column>
                <!-- Indicator link -->
                <b-table-column
                  field="indicator"
                  :label="$t('compliance-indicator-label')"
                  header-class="title-cell pb-8px"
                  cell-class="title-cell"
                >
                  <template v-slot:header="{ column }">
                    <span class="is-block is-size-7 has-text-grey has-text-weight-normal">{{ column.label }}</span>
                  </template>
                  <template v-slot="props">
                    <router-link
                      class="has-text-weight-medium"
                      :class="{
                        'has-text-grey-light': !isApplicable(props.row) || !indicatorIsActive(props.row)
                      }"
                      :to="getRoute(props.row)"
                    >
                      {{ props.row.title }}
                    </router-link>
                  </template>
                </b-table-column>
                <!-- Completed documents -->
                <b-table-column
                  :label="$t('compliance-documents-label')"
                  cell-class="has-text-left-mobile has-text-center-tablet compliance__nowrap"
                  header-class="pb-8px compliance__nowrap"
                  centered
                  :width="150"
                >
                  <template v-slot:header="{ column }">
                    <span class="is-block is-size-7 has-text-grey has-text-weight-normal">{{ column.label }}</span>
                  </template>
                  <template v-slot="props">
                    <span
                      v-if="indicatorIsActive(props.row) && props.row.stats"
                      class="compliance__nowrap documents-count is-block is-size-7 has-text-weight-bold"
                    >
                      {{ getDocumentsCount(props.row.stats) }}
                    </span>
                  </template>
                </b-table-column>
                <!-- Progress -->
                <b-table-column
                  field="progress"
                  :label="$t('compliance-progress-label')"
                  header-class="pr-0 pb-8px"
                  cell-class="status-badge-cell has-text-left-mobile has-text-right-tablet"
                  numeric
                >
                  <template v-slot:header="{ column }">
                    <span class="is-block is-size-7 has-text-grey has-text-weight-normal">{{ column.label }}</span>
                  </template>
                  <template v-slot="props">
                    <status-badge v-if="indicatorIsActive(props.row)" :indicator="props.row" />
                    <!--Rendre specifique / supprimer spécificités-->
                    <toggle-specific-indicator
                      v-else
                      :indicator="props.row"
                      :projectId="projectId"
                      size="is-small"
                      @on-set-specific="reloadCompliance()"
                      @on-remove-specific="reloadCompliance()"
                    />
                  </template>
                </b-table-column>
              </b-table>
            </div>
          </div>
        </b-collapse>
      </div>
    </div>
    <!-- Non conformité -->
    <template v-if="data && data.nonCompliancesIndicator">
      <h2 class="title is-4 has-text-weight-bold mt-32px">{{ $t('compliance-noncompliances') }}</h2>
      <div class="compliance__container">
        <div class="card-header is-shadowless" role="button">
          <router-link class="card-header-title has-text-weight-bold px-0" :to="getRoute(data && data.nonCompliancesIndicator)">
            <span class="level my-0" style="width: 100%">
              {{ data.nonCompliancesIndicator.title }}
            </span>
            <span class="card-header-icon has-text-primary pr-0 py-0">
              <b-icon icon="chevron-right"></b-icon>
            </span>
          </router-link>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import DifficultyIcon from '@/components/indicator/difficulty-icon'
import StatusBadge from '@/components/indicator/status-badge'
import StatusBadgeGroup from '@/components/indicator/status-badge-group'
import { URL_INDICATOR_GENERAL, URL_INDICATOR_PROJECT } from '@/consts/urls'
import IndicatorHelper from '@/helpers/indicator-helper'
import ToggleSpecificIndicator from '@/components/indicator/toggle-specific-indicator'

export default {
  components: { DifficultyIcon, StatusBadge, StatusBadgeGroup, ToggleSpecificIndicator },
  props: {
    data: Object,
    projectId: String,
    loading: Boolean,
    isProjectTemplate: Boolean,
    showSpecificOnly: Boolean
  },
  data() {
    return {
      collapseState: {}
    }
  },
  computed: {
    isGeneralCompliance() {
      return this.projectId === undefined
    },
    showToggleSpecificIndicator() {
      if (this.isGeneralCompliance) return false
      if (this.isProjectTemplate) return false
      return true
    }
  },
  methods: {
    isApplicable(indicator) {
      return IndicatorHelper.isApplicable(indicator)
    },
    sortIndicators(indicators) {
      let filteredIndicators = indicators
      if (this.showSpecificOnly) {
        filteredIndicators = indicators.filter((indicator) => indicator.isSpecific)
      }
      return [
        ...filteredIndicators.filter((x) => this.isApplicable(x)),
        ...filteredIndicators.filter((x) => !this.isApplicable(x))
      ]
    },
    getRoute(indicator) {
      if (this.projectId) {
        return { name: URL_INDICATOR_PROJECT, params: { id: indicator.id, projectId: this.projectId } }
      }
      return { name: URL_INDICATOR_GENERAL, params: { id: indicator.id } }
    },
    initCollapseState() {
      if (!this.data) return

      const defaultState = this.data.indicatorGroups.reduce((result, value) => {
        return {
          ...result,
          [value.groupName]: false
        }
      }, {})

      const historyState = history.state.collapseState

      this.collapseState = {
        ...defaultState,
        ...historyState
      }

      // Check drawers state to update toggle button value
      const allDrawersOpened = Object.keys(this.collapseState).every((k) => this.collapseState[k])
      this.updateDrawersState(allDrawersOpened)

      if (this.$route.query.from) {
        for (const group of this.data.indicatorGroups) {
          if (group.indicators.find((x) => x.id === this.$route.query.from)) {
            this.collapseState[group.groupName] = true
          }
        }
      }
    },
    updateDrawersState(allOpened) {
      this.$emit('drawers-state-updated', { allOpened })
    },
    openAll() {
      this.setAllDrawersState(true)
    },
    closeAll() {
      this.setAllDrawersState(false)
    },
    setAllDrawersState(opened) {
      if (!this.data) return

      const openAllState = this.data.indicatorGroups.reduce((result, value) => {
        return {
          ...result,
          [value.groupName]: opened
        }
      }, {})

      this.collapseState = {
        ...openAllState
      }
    },
    getDocumentsCount(stats) {
      if (stats.documentsCount > 0) return `${stats.completedDocumentsCount}/${stats.documentsCount}`
      return '-'
    },
    haveAtLeastOneSpecificIndicator(group) {
      return group.indicators.find((i) => i.isSpecific) != undefined
    },
    groupIsActive(group) {
      if (this.isGeneralCompliance) return true
      if (this.isProjectTemplate) return true
      return this.haveAtLeastOneSpecificIndicator(group)
    },
    indicatorIsActive(indicator) {
      if (this.isGeneralCompliance) return true
      if (this.isProjectTemplate) return true
      return indicator.isSpecific
    },
    reloadCompliance() {
      this.$emit('reload-compliance')
    },
    specificIndicatorCount(group) {
      return group.indicators.filter((x) => x.isSpecific === true).length
    },
    indicatorsGroupFiltered(group) {
      if (this.isGeneralCompliance || this.isProjectTemplate) return group.indicators
      return group.indicators.filter((x) => x.isSpecific === true)
    }
  },
  created() {
    this.initCollapseState()
  },
  watch: {
    data() {
      this.initCollapseState()
    },
    collapseState: {
      handler(value) {
        history.replaceState({ ...history.state, collapseState: value }, '')

        // Check drawers state to update toggle button value
        const allDrawersOpened = Object.keys(this.collapseState).every((k) => this.collapseState[k])
        this.updateDrawersState(allDrawersOpened)
      },
      deep: true
    }
  }
}
</script>

<fragments>
fragment complianceFields on ComplianceGraphType {
  nonCompliancesIndicator {
    id
    language
    title
  }
  indicatorGroups {
    groupName
    indicators {
      id
      language
      title
      difficulty
      status
      targetDate
      isSpecific
      stats {
        documentsCount
        completedDocumentsCount
      }
    }
  }
}
</fragments>

<style lang="scss">
.compliance {
  &__container {
    border-bottom: 1px solid $grey-lighter;
  }

  .card-header {
    border-top: 1px solid $grey-lighter;
  }

  .title-cell {
    width: 99%;
    text-align: right;
  }

  .status-badge-cell {
    min-width: 106px;
  }

  .difficulty-icon-cell {
    text-align: right;
  }

  @include tablet {
    &__nowrap {
      white-space: nowrap;
    }

    &__table-wrapper {
      margin-top: -1em;
    }

    .difficulty-icon-cell {
      padding-left: 0;
      padding-right: 0;
      text-align: left;
    }

    .title-cell {
      text-align: left;
    }

    .status-badge-cell {
      padding-right: 0;
    }

    .documents-count {
      padding-top: 2px; // Hack fix aligment
    }
  }

  // Impression
  @media print {
    .card-header-icon {
      display: none !important;
    }
    .collapse-content {
      display: block !important;
    }
    .card-content {
      padding-left: 0 !important;
    }
  }
}
</style>
